import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

export default function ClientDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [linking, setLinking] = useState(false);
  const [link, setLink] = useState({ ucc: "", pan: "", holding_nature: "SI" });
  const { admin } = useAdminAuth();

  const load = () => adminApi.get(`/admin/clients/${userId}`).then((r) => setClient(r.data));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => { try { const { data } = await adminApi.post(`/admin/clients/${userId}/approve`, {}); toast.success(data.bse2_ucc?.message || `UCC status: ${data.ucc_onboarding_status}`); load(); } catch (err) { toast.error(getApiErrorMessage(err)); } };
  const reject = async () => { try { await adminApi.post(`/admin/clients/${userId}/reject`, {}); toast.success("Rejected"); load(); } catch (err) { toast.error(getApiErrorMessage(err)); } };

  if (!client) return <AdminLayout><div className="py-16 text-center text-[var(--ink-muted)]">Loading...</div></AdminLayout>;

  const kyc = client.kyc_data || {};
  const linkExisting = async (event) => {
    event.preventDefault(); setLinking(true);
    try {
      const { data } = await adminApi.post(`/admin/clients/${userId}/link-bse-ucc`, { ...link, ucc: link.ucc.toUpperCase(), pan: link.pan.toUpperCase() });
      toast.success(`Verified and linked active BSE UCC ${data.bse_ucc}`); await load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setLinking(false); }
  };

  return (
    <AdminLayout>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-[var(--ink-muted)]" data-testid="admin-client-detail-back"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl text-[var(--brand)]">{client.full_name}</h1>
          <p className="text-sm text-[var(--ink-muted)]">{client.email} &middot; Joined {formatDate(client.created_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-[var(--ink-muted)]">UCC</span><StatusBadge status={client.ucc_onboarding_status || "NOT_STARTED"} /><span className="text-xs text-[var(--ink-muted)]">KYC</span><StatusBadge status={client.kyc_status || "NOT_VERIFIED"} /></div>
      </div>

      <Card className="mb-4 border-[var(--glass-border)]">
        <CardContent className="grid grid-cols-1 gap-4 text-sm min-[400px]:grid-cols-2 sm:grid-cols-3">
          {[
            ["Client Code", client.client_code || "-"], ["PAN", kyc.pan || "-"], ["DOB", kyc.dob || "-"],
            ["Address", kyc.address1 || "-"], ["City", kyc.city || "-"], ["State", kyc.state || "-"],
            ["Bank A/C", kyc.bank_account_no || "-"], ["IFSC", kyc.bank_ifsc || "-"], ["Mobile", client.phone || "-"],
          ].map(([label, value]) => (
            <div key={label}><div className="text-xs text-[var(--ink-muted)]">{label}</div><div className="font-medium">{value}</div></div>
          ))}
        </CardContent>
      </Card>

      {client.ucc_onboarding_status === "PENDING_REVIEW" && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button className="rounded-full bg-[var(--cta)]" onClick={approve} data-testid="admin-client-detail-approve"><CheckCircle2 className="mr-2 h-4 w-4" />Review and submit UCC to BSE</Button>
          <Button variant="outline" className="rounded-full" onClick={reject} data-testid="admin-client-detail-reject"><XCircle className="mr-2 h-4 w-4" />Reject</Button>
        </div>
      )}
      {admin?.role === "super_admin" && (
        <Card className="mt-6 border-[var(--glass-border)]">
          <CardContent className="p-6">
            <h2 className="font-heading text-lg text-[var(--brand)]">Link an existing active BSE UCC</h2>
            <p className="mt-1 text-xs text-[var(--ink-muted)]">BSE is queried first. The UCC is linked only when BSE reports it ACTIVE and the PAN matches this reviewed client record.</p>
            <form className="mt-4 grid gap-3 sm:grid-cols-3" onSubmit={linkExisting}>
              <div className="space-y-1.5"><Label>UCC</Label><Input required value={link.ucc} onChange={(e) => setLink((v) => ({ ...v, ucc: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>PAN</Label><Input required maxLength={10} value={link.pan} onChange={(e) => setLink((v) => ({ ...v, pan: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Holding nature</Label><Select value={link.holding_nature} onValueChange={(value) => setLink((v) => ({ ...v, holding_nature: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="SI">Single</SelectItem><SelectItem value="JO">Joint</SelectItem><SelectItem value="AS">Anyone or survivor</SelectItem></SelectContent></Select></div>
              <Button type="submit" disabled={linking} className="rounded-full bg-[var(--cta)] sm:col-span-3">{linking ? "Verifying with BSE…" : "Verify at BSE and link UCC"}</Button>
            </form>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
